import {
    CheckoutStep,
    CheckoutSteps,
} from "../global/apps/services/models/checkout.models";
import { Money } from "../global/apps/services/models/cart.models";
import {
    DeliveryLocation,
    DeliveryMethod,
} from "../global/apps/utils/API/Delivery/deliveryAPISchema";
import { Ga4Event, Ga4Events } from "../global/apps/services/models/gtm.models";
import { AddressDetails } from "Scripts/global/apps/utils/API/Bring/bringAPISchema";

export default class CheckoutHelpers {
    public static qualifiesForFreeShipping(
        freeShippingActive?: boolean,
        freeShippingLimit?: Money,
        subTotal?: Money,
    ): boolean {
        if (!freeShippingActive || !freeShippingLimit || !subTotal)
            return false;
        return (
            freeShippingActive && freeShippingLimit.amount <= subTotal.amount
        );
    }

    public static amountNeededToQualifyForFreeShipping(
        freeShippingActive?: boolean,
        freeShippingLimit?: Money,
        value?: Money,
    ): Money | undefined {
        if (
            !freeShippingActive ||
            !value ||
            !freeShippingLimit ||
            freeShippingLimit.amount <= value.amount
        )
            return;

        return {
            amount: freeShippingLimit.amount - value.amount,
            currency: value.currency,
        };
    }

    public static formatDeliveryLocationAddress(
        location: DeliveryLocation,
    ): string {
        return `${location.deliveryAddress?.trim()}, ${location.postalCode} ${location.city}`;
    }

    public static setInformationErrors(errors, informationErrors): void {
        this.clearErrors(informationErrors);

        errors.errors.forEach(
            (issue: { path: string | number; message: string }) => {
                informationErrors[issue.path].push(issue.message);
            },
        );
    }

    public static deliveryWindowTimeStamp(date: Date): string {
        const hours = date.getHours().toString().padStart(2, "0");
        const minutes = date.getMinutes().toString().padStart(2, "0");
        return `${hours}:${minutes}`;
    }

    public static formatDay(date: Date): string {
        if (this.isToday(date)) return "I dag";

        return date.toLocaleDateString("no-NB", {
            weekday: "long",
            day: "numeric",
            month: "long",
        });
    }

    private static isToday(date: Date): boolean {
        const today = new Date();
        return (
            date.getDate() === today.getDate() &&
            date.getMonth() === today.getMonth() &&
            date.getFullYear() === today.getFullYear()
        );
    }

    public static logoNameForDeliveryMethod(
        deliveryMethod: DeliveryMethod,
    ): string {
        switch (deliveryMethod.name) {
            case "Porterbuddy":
                return "porterbuddy";
            case "MyPack":
            case "HomeDelivery":
                return "postnord";
            default:
                return "";
        }
    }

    public static clearErrors(errorObject): void {
        Object.keys(errorObject).forEach((key) => (errorObject[key] = []));
    }

    public static hasDeliveryLocations(
        deliveryMethod: DeliveryMethod,
    ): boolean {
        return !!deliveryMethod?.deliveryLocations?.length;
    }

    public static hasDeliveryWindows(
        deliveryMethod: DeliveryMethod,
    ): boolean | undefined {
        return !!deliveryMethod?.deliveryWindows?.length;
    }

    public static sentenceCase(text: string): string {
        return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
    }

    public static toGoogleAnalyticsEventName(
        checkoutStep: CheckoutStep,
    ): Ga4Event {
        switch (checkoutStep) {
            case CheckoutSteps.CustomerInformation:
                return Ga4Events.BeginCheckout;
            case CheckoutSteps.Delivery:
                return Ga4Events.AddShippingInfoAddress;
            case CheckoutSteps.Payment:
                return Ga4Events.AddShippingInfo;
            case CheckoutSteps.FinalizeOrder:
                return Ga4Events.AddPaymentInfo;
            case CheckoutSteps.Confirmation:
                return Ga4Events.Purchase;
            default:
                return Ga4Events.NoEvent;
        }
    }

    /**
     * Returns the full street address as a string, e.g. "Main St 12B"
     */
    public static formatAddress(address: AddressDetails): string {
        const { streetName, houseNumber, letter } = address;
        return `${streetName} ${houseNumber}${letter ?? ""}`.trim();
    }

    public static getDifferences(obj1: any, obj2: any) {
        const diffs: Record<string, { storeValue: any; apiValue: any }> = {};
        const keys = new Set([
            ...Object.keys(obj1 || {}),
            ...Object.keys(obj2 || {}),
        ]);
        keys.forEach((key) => {
            if (JSON.stringify(obj1?.[key]) !== JSON.stringify(obj2?.[key])) {
                diffs[key] = { storeValue: obj1?.[key], apiValue: obj2?.[key] };
            }
        });
        return diffs;
    }
}
